import { AddHardwareFormValues } from '../../services/fetchServices/HardwareApi/addHardware';

export const addHardwareDtoInitialValues: AddHardwareFormValues = {
  type: 0,
  category: 0,
  attributeName: '',
  attributeValue: '',
  attributes: [],
  supplier: 0,
  brand: 0,
  organization: 0,
  assigneeType: 'user',
  assignee: 0,
  parentHardware: '',
  parentHardwareId: '',
  status: 'Free',
  model: '',
  serialNumbers: [{ serialNumber: '', jitTag: 'JIT-' }],
  warrantyPeriod: null,
  inventoryDate: null,
  purchasePrice: undefined,
  vatRate: '23',
  usableDepreciation: undefined,
  salesDate: null,
  purchaseDate: null,
  expansibility: true,
  comment: '',
  parentDialogOpen: false,
  formSourceId: null,
};

export type FormikValues = typeof addHardwareDtoInitialValues;

export function isPriceNumber(str: string) {
  const n = Math.round(Number(str) * 100) / 100;
  return (
    !Number.isNaN(n) &&
    n >= 0 &&
    (str.indexOf('.') === -1 || (n !== Infinity && n.toFixed(2) === str && n >= 0))
  );
}

export function isPositiveInteger(str: string) {
  const n = Math.floor(Number(str));
  return n !== Infinity && String(n) === str && n >= 0;
}

export function validateAddHardwareData(values: AddHardwareFormValues) {
  const errors: Partial<
    Record<Exclude<keyof AddHardwareFormValues, 'serialNumbers'>, string>
  > & { serialNumbers?: { serialNumber?: string; jitTag?: string }[] } = {};
  if (!values.type) {
    errors.type = 'required';
  }
  if (!values.category) {
    errors.category = 'required';
  }
  if (!values.brand) {
    errors.brand = 'required';
  }
  if (!values.supplier) {
    errors.supplier = 'required';
  }
  if (!values.organization) {
    errors.organization = 'required';
  }
  if (!values.model) {
    errors.model = 'required';
  }
  if (!values.serialNumbers || !values.serialNumbers[0]?.serialNumber.trim()) {
    errors.serialNumbers = [{ serialNumber: 'required' }];
  }
  if (values.vatRate && !isPriceNumber(values.vatRate)) {
    errors.vatRate = 'incorrect numeric value';
  }
  if (values.purchasePrice && !isPriceNumber(values.purchasePrice)) {
    errors.purchasePrice = 'incorrect numeric value';
  }
  if (values.usableDepreciation && !isPositiveInteger(values.usableDepreciation)) {
    errors.usableDepreciation = 'value must be a positive integer';
  }

  return errors;
}

type SerialNumberRow = AddHardwareFormValues['serialNumbers'][number];

const emptyJitTag = 'JIT-';

export function splitPastedLines(pastedText: string) {
  // a range copied from Excel ends with a newline; blank cells before it are kept
  return pastedText.replace(/\r?\n$/, '').split(/\r?\n/g);
}

/**
 * JIT tag for a pasted "serial number<TAB>JIT tag" line: an absent second column keeps the
 * existing tag, an explicitly empty cell clears it.
 */
export function pastedJitTag(tokens: string[], existingJitTag?: string) {
  if (tokens.length < 2) {
    return existingJitTag ?? emptyJitTag;
  }
  return tokens[1].trim() || emptyJitTag;
}

export function countPastedSerialNumbersAndJitTags(pastedText: string) {
  const rows = splitPastedLines(pastedText).map((line) =>
    line.split('\t').map((token) => token.trim()),
  );
  return {
    serialNumbers: rows.filter(([serialNumber]) => serialNumber).length,
    jitTags: rows.filter(([, jitTag]) => jitTag).length,
  };
}

/**
 * Pastes a column of JIT tags into the rows starting at `index`, one line per row. Blank cells
 * keep their row offset and clear that row's tag.
 */
export function applyPastedJitTags(
  rows: SerialNumberRow[],
  index: number,
  pastedText: string,
) {
  const jitTags = splitPastedLines(pastedText).map((line) => line.split('\t')[0].trim());
  let assigned = 0;
  const updatedRows = rows.map((row, rowIndex) => {
    const offset = rowIndex - index;
    if (offset < 0 || offset >= jitTags.length || !row.serialNumber.trim()) {
      return row;
    }
    assigned += 1;
    return { ...row, jitTag: jitTags[offset] || emptyJitTag };
  });
  return {
    rows: updatedRows,
    pastedJitTags: jitTags.length,
    rowsWithSerialNumber: rows
      .slice(index)
      .filter(({ serialNumber }) => serialNumber.trim()).length,
    ignoredJitTags: jitTags.length - assigned,
  };
}
